import { PerspectiveCamera } from "three";
import { mix, pow, uniform, vec3, vec4 } from "three/tsl";
import type {
  TslEffect,
  TslEffectContext,
} from "../../../../engine/postprocessing/PostProcessingController";
import type { SsgiSparseOutputMode } from "../../RenderSettingsStore";
import {
  SPARSE_GI_V2_STRIDES,
  sparseGiV2,
} from "./sparse-gi-v2/SparseGiV2Node.js";

// TSL düğümleri tipli değil; okunur kalsın diye `any`.
/* eslint-disable @typescript-eslint/no-explicit-any */
type TslNode = any;

export type SparseGiV2Options = {
  output: SsgiSparseOutputMode;
  sliceCount: number;
  stepCount: number;
  radius: number;
  thickness: number;
  expFactor: number;
  giIntensity: number;
  backfaceLighting: number;
  useScreenSpaceSampling: boolean;
  useLinearThickness: boolean;
  /** Hücre kenarı (piksel): 2, 4, 6, 8, 16 ya da 32. */
  stride: number;
  /** Bir pikselde en fazla kaç kare birikir (1 = birikim yok). */
  historyLength: number;
  /** Yumuşatma turu (0–3). */
  denoisePasses: number;
  /** Komşu noktası tutmayan pikselleri ayrıca tara. */
  edgeRefine: boolean;
  /** AO üssü (0 = AO yok). */
  aoIntensity: number;
};

const DEFAULTS: SparseGiV2Options = {
  output: "combined",
  sliceCount: 2,
  stepCount: 8,
  radius: 12,
  thickness: 1,
  expFactor: 2,
  giIntensity: 10,
  backfaceLighting: 0,
  useScreenSpaceSampling: true,
  useLinearThickness: false,
  stride: 4,
  historyLength: 32,
  denoisePasses: 2,
  edgeRefine: true,
  aoIntensity: 1,
};

// Bunlar değişince GI sonucu değişir; eski birikim beklenmeden silinir.
const RESET_KEYS: (keyof SparseGiV2Options)[] = [
  "sliceCount",
  "stepCount",
  "radius",
  "thickness",
  "expFactor",
  "giIntensity",
  "backfaceLighting",
  "useScreenSpaceSampling",
  "useLinearThickness",
];

function nearestStride(value: number): number {
  return SPARSE_GI_V2_STRIDES.reduce((best: number, s: number) =>
    Math.abs(s - value) < Math.abs(best - value) ? s : best,
  );
}

function asColorNode(input: TslNode): TslNode {
  return typeof input?.getTextureNode === "function"
    ? input.getTextureNode()
    : input;
}

/**
 * Seyrek GI v2: küçük hedefte tarama + kayan nokta + yalnız GI'ya birikim.
 * TRAA kullanmaz; kenar yumuşatma için ayrı TAA açılabilir.
 * Ayrıntı: sparse-gi-v2/SparseGiV2Node.js
 */
export class SparseGiV2TslEffect implements TslEffect {
  readonly order = 10;
  readonly branch = "main" as const;
  readonly needsNormal = true;
  readonly needsDiffuse = true;
  readonly needsVelocity = false;

  private _options: SparseGiV2Options;
  private _node: TslNode | undefined;
  private readonly _aoIntensity = uniform(1);

  constructor(options: Partial<SparseGiV2Options> = {}) {
    this._options = { ...DEFAULTS, ...definedOnly(options) };
  }

  /** @returns true → efekt zinciri yeniden kurulmalı (çıktı türü değişti). */
  setParameters(partial: Partial<SparseGiV2Options>): boolean {
    const before = this._options;
    this._options = { ...before, ...definedOnly(partial) };
    const resetHistory = RESET_KEYS.some(
      (key) => this._options[key] !== before[key],
    );
    this._syncNode(resetHistory);
    return this._options.output !== before.output;
  }

  private _syncNode(resetHistory: boolean): void {
    const o = this._options;
    this._aoIntensity.value = o.aoIntensity;
    const node = this._node;
    if (!node) return;
    node.sliceCount.value = o.sliceCount;
    node.stepCount.value = o.stepCount;
    node.radius.value = o.radius;
    node.thickness.value = o.thickness;
    node.expFactor.value = o.expFactor;
    node.giIntensity.value = o.giIntensity;
    node.backfaceLighting.value = o.backfaceLighting;
    node.useScreenSpaceSampling.value = o.useScreenSpaceSampling;
    node.useLinearThickness.value = o.useLinearThickness;
    node.historyLength.value = Math.max(1, Math.round(o.historyLength));
    node.stride = nearestStride(o.stride);
    node.denoisePasses = o.denoisePasses;
    node.edgeRefine = o.edgeRefine;
    if (resetHistory) node.resetHistory();
  }

  dispose(): void {
    this._node?.dispose();
    this._node = undefined;
  }

  compose(input: unknown, ctx: TslEffectContext): unknown {
    const beauty = asColorNode(input);
    if (!(ctx.camera instanceof PerspectiveCamera)) return beauty;
    if (!ctx.depth || !ctx.normal) return beauty;
    if (this._options.output === "direct") return beauty;

    this._node?.dispose();
    this._node = sparseGiV2(beauty, ctx.depth, ctx.normal, ctx.camera);
    this._syncNode(false);

    const giAo = this._node.getTextureNode();
    const gi = giAo.rgb;
    const ao = pow(giAo.a, this._aoIntensity);

    switch (this._options.output) {
      case "gi":
        return vec4(gi, 1);
      case "ao":
        return vec4(vec3(ao), 1);
      case "debug":
        return this._node.getDebugNode();
      case "source":
        return this._node.getSourceDebugNode();
    }

    // Direkt ışık × AO + albedo × GI. Alfa 0 (boş arka plan) olduğu gibi kalır.
    const albedo = asColorNode(ctx.diffuse ?? beauty);
    const lit = beauty.rgb.mul(ao).add(albedo.rgb.mul(gi));
    return vec4(mix(beauty.rgb, lit, beauty.a), beauty.a);
  }
}

function definedOnly<T extends object>(partial: Partial<T>): Partial<T> {
  return Object.fromEntries(
    Object.entries(partial).filter(([, value]) => value != null),
  ) as Partial<T>;
}
